/** Formas de los documentos que viajan a Firestore. */

import type {
  TipoVenta,
  CodigoPlanta,
  EstadoGuia,
  Sector,
  Turno,
  UnidadItem,
} from '@/domain/tipos';
import type { UnidadCantidad } from '@/domain/unidades';

/** Campos de auditoría comunes a todo documento. */
export interface Meta {
  creadoEn: string; // ISO
  creadoPor: string | null;
  actualizadoEn: string;
  actualizadoPor: string | null;
  version: number;
  eliminadoEn: string | null;
}

export interface DocCatalogo extends Meta {
  id: string;
  codigo: string;
  nombre: string;
  alias: string[];
  activo: boolean;
}

export interface DocPlanta extends DocCatalogo {
  codigoPlanta: CodigoPlanta;
  sector: Sector;
  minCargaM3: number;
}

export interface DocProducto extends DocCatalogo {
  codigoNormalizado: string;
  familia: string;
  grado: string | null;
  mpa: number | null;
  pctConfianza: number | null;
  diametroArido: number | null;
  cono: number | null;
  extras: string[];
  parseOk: boolean;
  generaRecargos: boolean;
}

export interface DocCentroCosto extends DocCatalogo {
  tipo: 'GRAFO_CODELCO' | 'VENTA_PARTICULAR' | 'OC' | 'CC' | 'OTRO';
  proyectoId: string | null;
}

export interface DocCapitulo extends Meta {
  id: string;
  numero: number;
  nombre: string;
  orden: number;
}

export interface DocItemContrato extends Meta {
  id: string;
  codigo: string;
  capituloNumero: number;
  descripcion: string;
  unidad: UnidadItem;
  cantidadContratada: number;
  precioBaseUf: number;
  aplicaReajuste: boolean;
  esRecargo: boolean;
  activo: boolean;
}

/** Guía tal como se guarda. Las marcas van como ISO para que Firestore las ordene. */
export interface DocGuia extends Meta {
  id: string;
  plantaId: string;
  plantaCodigo: CodigoPlanta;
  sector: Sector;
  folio: number;
  remito: number | null;
  fecha: string; // yyyy-MM-dd
  turno: Turno | null;

  clienteId: string | null;
  clienteNombre: string | null;
  productoId: string | null;
  productoCodigo: string | null;
  cantidadM3: number;

  centroCostoId: string | null;
  centroCostoCodigo: string | null;
  proyectoId: string | null;
  proyectoNombre: string | null;
  destinoId: string | null;
  destinoTexto: string | null;

  equipoId: string | null;
  equipoCodigo: string | null;
  operadorMixerId: string | null;
  operadorMixerNombre: string | null;
  operadorPlantaId: string | null;
  operadorPlantaNombre: string | null;
  solicitante: string | null;
  observaciones: string | null;

  tsSalidaPlanta: string | null;
  tsLlegadaObra: string | null;
  tsInicioDescarga: string | null;
  tsTerminoDescarga: string | null;
  tsSalidaObra: string | null;
  tsLlegadaPlanta: string | null;

  itemContratoId: string | null;
  itemContratoCodigo: string | null;

  /** En qué se contó la cantidad. Las guías viejas no lo traen: son m³. */
  unidadCantidad: UnidadCantidad;
  /** Contrato CODELCO o venta directa a un tercero. */
  tipoVenta: TipoVenta;
  /**
   * A qué estado de pago la asignó la planilla de origen.
   *
   * Las Planillas Generales lo declaran en una columna: una guía atrasada
   * puede tener fecha de agosto y estar cobrada en el EP de septiembre. Sin
   * este dato habría que adivinarlo por la fecha, y entonces el resultado
   * depende del orden en que se carguen los archivos. `null` en lo que se
   * carga desde la app: ahí manda la fecha.
   */
  epDeclarado: number | null;
  /**
   * Histórico: se carga para poder consultarlo, no para cobrarlo.
   *
   * El libro de guías de los años anteriores ya se facturó por fuera de la
   * app, y sus cifras no coinciden con las de los estados de pago que se
   * emitieron. Sirve para buscar —qué se despachó, a quién, cuándo— pero no
   * puede entrar a ningún estado de pago ni arrastrarse como guía atrasada.
   */
  soloConsulta: boolean;

  estado: EstadoGuia;
  motivoAnulacion: string | null;
  estadoPagoId: string | null;
  origen: 'APP' | 'IMPORT_EXCEL' | 'INFORME_DIARIO';

  /** Derivados, guardados para poder filtrar y sumar sin recalcular. */
  minEstadiaObra: number | null;
  minSobreEstadia: number | null;
  bloquesCodelco: number | null;
  tiemposCompletos: boolean;
  montoRecargosUf: number;
}

export function metaNueva(usuarioId: string | null): Meta {
  const ahora = new Date().toISOString();
  return {
    creadoEn: ahora,
    creadoPor: usuarioId,
    actualizadoEn: ahora,
    actualizadoPor: usuarioId,
    version: 1,
    eliminadoEn: null,
  };
}

/**
 * UUID v7: ordenable por tiempo y generado en el cliente.
 * Que el id lo ponga el cliente es lo que hace idempotente el reintento
 * de sincronización: el mismo registro nunca se crea dos veces.
 */
export function uuidv7(): string {
  const ms = Date.now();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  bytes[0] = (ms / 2 ** 40) & 0xff;
  bytes[1] = (ms / 2 ** 32) & 0xff;
  bytes[2] = (ms / 2 ** 24) & 0xff;
  bytes[3] = (ms / 2 ** 16) & 0xff;
  bytes[4] = (ms / 2 ** 8) & 0xff;
  bytes[5] = ms & 0xff;
  bytes[6] = (bytes[6]! & 0x0f) | 0x70; // versión 7
  bytes[8] = (bytes[8]! & 0x3f) | 0x80; // variante

  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
